import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCircle, KeyRound, ImagePlus, QrCode } from "lucide-react";
import api, { apiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PlatformSettings } from "@/components/PlatformSettings";

export default function Settings() {
  const { user, setUser } = useAuth();
  const isParent = user.role === "orang_tua";
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(isParent ? "" : user.email);
  const [savingProfile, setSavingProfile] = useState(false);
  const [phone, setPhone] = useState(user.phone || "");
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const isStaff = ["super_admin", "kepala_sekolah", "bendahara"].includes(user.role);
  const canEditLogo = ["super_admin", "bendahara"].includes(user.role);
  const [logoPath, setLogoPath] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [payInfo, setPayInfo] = useState({ bank_name: "", account_number: "", account_name: "" });
  const [qrisPath, setQrisPath] = useState(null);
  const [qrisFile, setQrisFile] = useState(null);
  const [savingPay, setSavingPay] = useState(false);
  const [uploadingQris, setUploadingQris] = useState(false);
  const [payLoaded, setPayLoaded] = useState(false);

  useEffect(() => {
    if (isStaff) {
      api.get("/schools/current").then((r) => {
        setLogoPath(r.data?.logo_path || null);
        setQrisPath(r.data?.qris_path || null);
        setPayInfo(r.data?.payment_info || { bank_name: "", account_number: "", account_name: "" });
        setPayLoaded(true);
      }).catch(() => setPayLoaded(true));
    }
  }, []);

  const savePayInfo = async (e) => {
    e.preventDefault();
    setSavingPay(true);
    try {
      await api.put("/schools/current/payment-info", payInfo);
      toast.success("Info pembayaran berhasil disimpan");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPay(false);
    }
  };

  const handleUploadQris = async (e) => {
    e.preventDefault();
    if (!qrisFile) return;
    setUploadingQris(true);
    try {
      const fd = new FormData();
      fd.append("qris", qrisFile);
      const r = await api.post("/schools/qris", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setQrisPath(r.data.qris_path);
      setQrisFile(null);
      toast.success("Gambar QRIS berhasil diperbarui");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingQris(false);
    }
  };

  const handleUploadLogo = async (e) => {
    e.preventDefault();
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("logo", logoFile);
      const r = await api.post("/schools/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPath(r.data.logo_path);
      setLogoFile(null);
      window.dispatchEvent(new Event("sb-logo-updated"));
      toast.success("Logo sekolah berhasil diperbarui");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingLogo(false);
    }
  };

  const logoUrl = logoPath
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${logoPath}?auth=${localStorage.getItem("sb_token")}&t=${Date.now()}`
    : null;
  const qrisUrl = qrisPath
    ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${qrisPath}?auth=${localStorage.getItem("sb_token")}&t=${Date.now()}`
    : null;

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const r = await api.put("/auth/profile", { name, email: email || null, phone: phone });
      setUser(r.data);
      toast.success("Profil berhasil diperbarui");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Konfirmasi kata sandi baru tidak cocok");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Kata sandi berhasil diubah");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Pengaturan Akun</h1>
        <p className="mt-1 text-sm text-slate-500">Kelola profil dan keamanan akun Anda.</p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#E5F0FA]">
            <UserCircle className="h-6 w-6 text-[#0060AF]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800" data-testid="settings-user-name">{user.name}</p>
            <div className="mt-0.5 flex items-center gap-2">
              <Badge className="rounded-full bg-[#E5F0FA] text-[#0060AF] hover:bg-[#E5F0FA]" data-testid="settings-role-badge">{ROLE_LABELS[user.role]}</Badge>
              {isParent && user.nisn && (
                <span className="text-xs text-slate-500" data-testid="settings-nisn-info">Login Anda: NISN {user.nisn}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading text-base font-semibold text-slate-800">Profil</h3>
          <form onSubmit={saveProfile} className="mt-4 space-y-4" data-testid="profile-form">
            <div className="space-y-2">
              <Label htmlFor="set-name">Nama Lengkap</Label>
              <Input id="set-name" data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            {!isParent && (
              <div className="space-y-2">
                <Label htmlFor="set-email">Email</Label>
                <Input id="set-email" type="email" data-testid="settings-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
            )}
            {isParent && (
              <div className="space-y-2">
                <Label htmlFor="set-email-ortu">Email (opsional)</Label>
                <Input id="set-email-ortu" type="email" data-testid="settings-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Isi jika ingin bisa login dengan email" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="set-phone">No WhatsApp {user.role === "super_admin" && <span className="text-xs text-slate-400">(ditampilkan sebagai kontak admin langganan)</span>}</Label>
              <Input id="set-phone" data-testid="settings-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Contoh: 081234567890" />
            </div>
            <Button type="submit" disabled={savingProfile} data-testid="settings-profile-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
              {savingProfile ? "Menyimpan..." : "Simpan Profil"}
            </Button>
          </form>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <h3 className="font-heading text-base font-semibold text-slate-800">Ganti Kata Sandi</h3>
          </div>
          {isParent && user.must_change_password && (
            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="settings-must-change-pw">
              Anda masih memakai kata sandi awal (NISN). Segera ganti demi keamanan.
            </div>
          )}
          <form onSubmit={savePassword} className="mt-4 space-y-4" data-testid="password-form">
            <div className="space-y-2">
              <Label htmlFor="pw-current">Kata Sandi Saat Ini</Label>
              <Input id="pw-current" type="password" data-testid="settings-current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-new">Kata Sandi Baru</Label>
              <Input id="pw-new" type="password" data-testid="settings-new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required minLength={6} placeholder="Minimal 6 karakter" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-confirm">Konfirmasi Kata Sandi Baru</Label>
              <Input id="pw-confirm" type="password" data-testid="settings-confirm-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required />
            </div>
            <Button type="submit" disabled={savingPw} data-testid="settings-password-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
              {savingPw ? "Menyimpan..." : "Ganti Kata Sandi"}
            </Button>
          </form>
        </div>
      </div>

      {isStaff && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="logo-section">
          <div className="flex items-center gap-2">
            <ImagePlus className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <h3 className="font-heading text-base font-semibold text-slate-800">Logo Sekolah</h3>
          </div>
          <p className="mt-1 text-xs text-slate-500">Logo tampil di sidebar aplikasi. Format jpg/png/webp, maksimal 2 MB.</p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo sekolah" className="h-16 w-16 rounded-lg border border-slate-200 object-cover" data-testid="logo-preview" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-slate-300 text-xs text-slate-400" data-testid="logo-empty">
                Belum ada
              </div>
            )}
            {canEditLogo && (
              <form onSubmit={handleUploadLogo} className="flex flex-wrap items-center gap-3">
                <Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} data-testid="logo-file-input" className="max-w-xs" />
                <Button type="submit" disabled={uploadingLogo || !logoFile} data-testid="logo-upload-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                  {uploadingLogo ? "Mengunggah..." : "Unggah Logo"}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
      {canEditLogo && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="payment-info-section">
          <div className="flex items-center gap-2">
            <QrCode className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <h3 className="font-heading text-base font-semibold text-slate-800">Pembayaran Online (Transfer & QRIS)</h3>
          </div>
          <p className="mt-1 text-xs text-slate-500">Info ini ditampilkan ke orang tua saat mereka membayar SPP/tagihan secara online.</p>
          <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <form onSubmit={savePayInfo} className="space-y-4" data-testid="payment-info-form">
              {!payLoaded && <p className="text-xs text-slate-400">Memuat info pembayaran...</p>}
              <fieldset disabled={!payLoaded} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pay-bank">Nama Bank / E-Wallet</Label>
                <Input id="pay-bank" data-testid="payment-bank-input" value={payInfo.bank_name} onChange={(e) => setPayInfo({ ...payInfo, bank_name: e.target.value })} placeholder="Contoh: BSI / BCA / DANA" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay-acc">Nomor Rekening / E-Wallet</Label>
                <Input id="pay-acc" data-testid="payment-account-input" value={payInfo.account_number} onChange={(e) => setPayInfo({ ...payInfo, account_number: e.target.value })} placeholder="Contoh: 7123456789" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pay-name">Atas Nama</Label>
                <Input id="pay-name" data-testid="payment-name-input" value={payInfo.account_name} onChange={(e) => setPayInfo({ ...payInfo, account_name: e.target.value })} placeholder="Contoh: Bendahara SDN 1" />
              </div>
              <Button type="submit" disabled={savingPay || !payLoaded} data-testid="payment-info-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {savingPay ? "Menyimpan..." : "Simpan Info Pembayaran"}
              </Button>
              </fieldset>
            </form>
            <div>
              <Label>Gambar QRIS Sekolah</Label>
              <div className="mt-2 flex flex-wrap items-center gap-4">
                {qrisUrl ? (
                  <img src={qrisUrl} alt="QRIS sekolah" className="h-32 w-32 rounded-lg border border-slate-200 object-cover" data-testid="qris-preview" />
                ) : (
                  <div className="flex h-32 w-32 items-center justify-center rounded-lg border border-dashed border-slate-300 text-center text-xs text-slate-400" data-testid="qris-empty">Belum ada QRIS</div>
                )}
                <form onSubmit={handleUploadQris} className="flex flex-col gap-3">
                  <Input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setQrisFile(e.target.files?.[0] || null)} data-testid="qris-file-input" className="max-w-xs" />
                  <Button type="submit" disabled={uploadingQris || !qrisFile} data-testid="qris-upload-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                    {uploadingQris ? "Mengunggah..." : "Unggah QRIS"}
                  </Button>
                </form>
              </div>
              <p className="mt-2 text-xs text-slate-400">Format jpg/png/webp, maksimal 2 MB.</p>
            </div>
          </div>
        </div>
      )}

      {user.role === "super_admin" && <PlatformSettings />}
    </div>
  );
}
